import { MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, Resolver, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { useApplications } from "../../../../hooks/useApplications"
import { useTrainings } from "../../../../hooks/useTrainings"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import {
  createTrainingValidationSchema,
  useTrainingFieldRenderers,
} from "../../../components/Helpers"
import { AccessLevel, DimaFeatureShortNameEnum } from "../../../models/Authorization"
import { TrainingPost } from "../../../models/Training"
import { ControllerRendererProps } from "../../../types/reactHookForm"

const returnURL = `/trainings`

export function TrainingCreate() {
  const { applicationsList } = useApplications(
    DimaFeatureShortNameEnum.TM,
    AccessLevel.Write
  )
  const navigate = useNavigate()
  const { trainingsList } = useTrainings()

  const validationSchema = useMemo(
    () =>
      createTrainingValidationSchema(
        trainingsList.map((training) => training.name),
        { includeApplicationId: true }
      ),
    [trainingsList]
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<TrainingPost>({
    resolver: zodResolver(validationSchema) as Resolver<TrainingPost>,
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      applicationId: "",
      name: "",
      title: "",
      description: "",
      imageUrl: "",
      deviceImageUrl: "",
      summary: "",
      disclaimer: "",
      completionMessage: "",
      expiryReminderDays: null,
    })
  }, [reset])

  const onSubmit = useCallback(
    (values: TrainingPost) =>
      digitalManagerApi
        .post(`/trainings`, {
          applicationId: values.applicationId,
          name: values.name,
          title: values.title,
          description: values.description,
          imageUrl: values.imageUrl,
          deviceImageUrl: values.deviceImageUrl,
          summary: values.summary,
          disclaimer: values.disclaimer,
          completionMessage: values.completionMessage,
          validityMonths: values.validityMonths,
          expiryReminderDays: values.expiryReminderDays,
        })
        .then((res) => {
          if (res.status === StatusCodes.CREATED) {
            toast.success("Training created")
            navigate(returnURL)
          }
        })
        .catch(() => {
          toast.error("Cannot create training")
        }),
    [navigate]
  )

  const handleApplicationSelectChange = useCallback(
    (field: ControllerRenderProps<TrainingPost, "applicationId">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderApplicationSelect = useCallback(
    ({ field }: ControllerRendererProps<TrainingPost, "applicationId">) => (
      <MUISelect
        name={field.name}
        id="applicationId"
        size={32}
        dropdownLOV={applicationsList.map((a) => ({
          id: String(a.id),
          name: a.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleApplicationSelectChange(field)}
        onBlur={field.onBlur}
      />
    ),
    [applicationsList, handleApplicationSelectChange]
  )

  const { renderTextField, renderNumberField } = useTrainingFieldRenderers({ errors })

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Application">
        <>
          <Controller
            name="applicationId"
            control={control}
            render={renderApplicationSelect}
          />
          {errors.applicationId && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                applicationId: {
                  message:
                    typeof errors.applicationId?.message === "string"
                      ? errors.applicationId.message
                      : "",
                },
              }}
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Title">
        <Controller name="title" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Description">
        <Controller name="description" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Image URL">
        <Controller name="imageUrl" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Device image URL">
        <Controller name="deviceImageUrl" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Summary">
        <Controller name="summary" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Disclaimer">
        <Controller name="disclaimer" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Completion message">
        <Controller name="completionMessage" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Validity months">
        <Controller name="validityMonths" control={control} render={renderNumberField} />
      </FieldRow>
      <FieldRow label="Expiry reminder days">
        <Controller
          name="expiryReminderDays"
          control={control}
          render={renderNumberField}
        />
      </FieldRow>
    </FormPage>
  )
}
